import { z } from 'zod'

import { StatusReport } from './index'

import { Pagination } from 'app/types/app/request'
import { Product } from 'app/types/catalogue/product'
import { Manager, ManagerBinding } from 'app/types/park/manager'
import { PaymentMethodSale, Sale } from 'app/types/transactions/sale'
import { KonviveBinding } from 'app/types/users/konvive'

/** Type of VAT product to export. */
export const TypeProductVatReport = z.enum(['REFUND', 'SALE'])
export type TypeProductVatReport = z.infer<typeof TypeProductVatReport>

/** Sale vat export. */
export const SaleVatReport = Sale.pick({
    id: true,
}).extend({
    date: z.string().date(),
})

/** Product VAT to export with added data returned by the API. */
const ProductVatReport = Product.pick({
    id: true,
    name: true,
    ean: true,
}).extend({
    type: TypeProductVatReport,
    // TODO: Pick instead of renaming when the name will be changed
    vat: Product.shape.tva,
    sale: SaleVatReport,
    manager_item_code: z.string(),
    konvive: KonviveBinding.optional(),
    terminal: z.string(),
    payment_method: PaymentMethodSale,
    card_type: z.string(),
    amount_without_vat: z.number(),
    amount_vat: z.number(),
    amount_with_vat: z.number(),
})

/** The VAT report with products data returned by the API. */
export const InitialObjectVatReport = z.object({
    vat: Product.shape.tva,
    products: ProductVatReport.array().min(1),
})

export type InitialObjectVatReport = z.infer<typeof InitialObjectVatReport>

/** The monthly VAT export returned by the API. */
const InitialMonthVatReport = z.object({
    date: z.string().date(),

    /** The VAT list of products VAT. */
    vats: InitialObjectVatReport.array(),
})

/** The VAT report returned by the API.*/
export const InitialVatReport = z.object({
    id: z.number().positive(),
    creation_date: z.string().datetime({ offset: true }),
    manager: ManagerBinding,
    date_start: z.string().date(),
    date_end: z.string().date(),
    status: StatusReport,
    /** List of months VAT export data. */
    months: InitialMonthVatReport.array().min(1),
})

export type InitialVatReport = z.infer<typeof InitialVatReport>

/** The VAT report list requested. */
export const InitialVatReportsRequest = Pagination.extend({
    results: InitialVatReport.array(),
})

export type InitialVatReportsRequest = z.infer<typeof InitialVatReportsRequest>

/** The VAT report with products with added data displayed in tables. */
const ObjectVatReport = InitialObjectVatReport.extend({
    amount_without_vat: z.number().positive(),
    amount_vat: z.number().positive(),
    amount_with_vat: z.number().positive(),
})

/** The monthly VAT export with added data displayed in tables. */
const MonthVatReport = InitialMonthVatReport.pick({
    date: true,
}).extend({
    vats: ObjectVatReport.array(),
    amount_without_vat: z.number().positive(),
    amount_vat: z.number().positive(),
    amount_with_vat: z.number().positive(),
})

export type MonthVatReport = z.infer<typeof MonthVatReport>

/** The vat report object with added data displayed in tables. */
export const VatReport = InitialVatReport.pick({
    id: true,
    creation_date: true,
    manager: true,
    date_start: true,
    date_end: true,
    status: true,
}).extend({
    months: MonthVatReport.array().min(1),
    amount_without_vat: z.number().positive(),
    amount_vat: z.number().positive(),
    amount_with_vat: z.number().positive(),
})

export type VatReport = z.infer<typeof VatReport>

/** The product VAT export with added data displayed in tables.*/
export const ProductVatReportDisplayed = ProductVatReport.extend({
    isOdd: z.boolean(),
    /** The Vat summary to display. */
    vatSummary: z
        .object({
            vat: ObjectVatReport.shape.vat,
            amount_without_vat: ObjectVatReport.shape.amount_without_vat,
            amount_vat: ObjectVatReport.shape.amount_vat,
            amount_with_vat: ObjectVatReport.shape.amount_with_vat,
        })
        .optional(),

    /** The month summary to display. */
    monthSummary: MonthVatReport.pick({
        date: true,
        amount_without_vat: true,
        amount_vat: true,
        amount_with_vat: true,
    }).optional(),
}).extend({
    id: z.string().cuid2().optional(),
    productId: Product.shape.id,
})

export type ProductVatReportDisplayed = z.infer<
    typeof ProductVatReportDisplayed
>

/** The Vat report with only months and product data to display in split mode. */
export const MonthVatReportSplit = MonthVatReport.pick({
    date: true,
}).extend({
    products: ProductVatReportDisplayed.array(),
})

export type MonthVatReportSplit = z.infer<typeof MonthVatReportSplit>

/** The data sent to create a vat report. */
export const VatReportPayload = InitialVatReport.pick({
    date_start: true,
    date_end: true,
}).extend({
    manager: Manager.shape.id.optional(),
})

export type VatReportPayload = z.infer<typeof VatReportPayload>
